import React, { useCallback, useMemo } from 'react';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { CopilotOutput } from 'libs/end-to-end/src/copilot';
import { HiringCopilotRequest } from 'libs/end-to-end/src/custom-viz/HiringCopilotTypes';
import { PromotionCopilotRequest } from 'libs/end-to-end/src/custom-viz/PromotionCopilotTypes';
import { ShareLinkModal } from 'apps/sigma2-ui/src/components/ui/ShareLinkModal';
import { useCustomViz } from 'apps/sigma2-ui/src/components/custom-viz/hooks/useCustomViz';
import { CopilotPoolView } from 'apps/sigma2-ui/src/copilot/components/CopilotPoolView';
import { checkLoadingStatus } from 'apps/sigma2-ui/src/custom-viz/lib/checkLoadingStatus';
import { SlateCopilotDataPoint } from 'libs/end-to-end/src/copilot';
import { SlatePoolSubject } from 'libs/end-to-end/src/copilot';
import { BasicCopilotAddToSlateFn } from 'apps/sigma2-ui/src/copilot/hooks/useBasicCopilotAddToSlate';
import { BasicCopilotRemoveFromSlateFn } from 'apps/sigma2-ui/src/copilot/hooks/useBasicCopilotRemoveFromSlate';
import { Nullable } from 'libs/end-to-end/src/ui-types';
import { Setter } from 'libs/end-to-end/src/ui-types';
import { CustomVizCircularSpinner } from 'apps/sigma2-ui/src/components/ui/CustomVizCircularSpinner';
import { CustomVizRequest } from 'libs/end-to-end/src/custom-viz/CustomVizRequest';
import { UserAnalysesOverlay } from 'apps/sigma2-ui/src/components/UserAnalysesOverlay';
import { OnUserAnalysisCancelCallback } from 'apps/sigma2-ui/src/lib/hooks/useOnUserAnalysisCancelCallback';

export const CopilotChart = ({
  focusArea,
  subjectsWithSlateStatus,
  slateCopilotDataPoints,
  addToSlate,
  removeFromSlate,
  showShareLinkModal,
  setShowShareLinkModal,
  currentShareLink,
  showGraphTooltip,
  setShowGraphTooltip,
  setNextSubjectIdToScrollTo,
  currentlySelectedSubjectId,
  setCurrentlySelectedSubjectId,
  selectedDepartments,
  setSelectedDepartments,
  selectedRoles,
  setSelectedRoles,
  selectedLocations,
  setSelectedLocations,
  departments,
  applicableRoles,
  locations,
  selectedStartDate,
  selectedEndDate,
  setSelectedStartDate,
  setSelectedEndDate,
  canAnalyze,
  currentCustomVizRequestToExecute,
  onUserAnalysisCancel,
}: {
  focusArea: FocusArea;
  subjectsWithSlateStatus: SlatePoolSubject[];
  slateCopilotDataPoints: SlateCopilotDataPoint[];
  addToSlate: BasicCopilotAddToSlateFn;
  removeFromSlate: BasicCopilotRemoveFromSlateFn;
  showShareLinkModal: boolean;
  setShowShareLinkModal: Setter<boolean>;
  currentShareLink: string | null;
  showGraphTooltip: boolean;
  setShowGraphTooltip: Setter<boolean>;
  setNextSubjectIdToScrollTo: Setter<string | null>;
  currentlySelectedSubjectId: string | null;
  setCurrentlySelectedSubjectId: Setter<string | null>;
  selectedDepartments: string[];
  setSelectedDepartments: Setter<string[]>;
  selectedRoles: string[];
  setSelectedRoles: Setter<string[]>;
  selectedLocations: string[];
  setSelectedLocations: Setter<string[]>;
  departments: string[];
  applicableRoles: string[];
  locations: string[];
  selectedStartDate: Date | undefined;
  selectedEndDate: Date | undefined;
  setSelectedStartDate: Setter<Date | undefined>;
  setSelectedEndDate: Setter<Date | undefined>;
  canAnalyze: boolean;
  currentCustomVizRequestToExecute:
    | HiringCopilotRequest
    | PromotionCopilotRequest
    | null;
  onUserAnalysisCancel: OnUserAnalysisCancelCallback;
}) => {
  const customVizContext = useCustomViz();
  const { status, doViz } = customVizContext;

  const outputParsingErrorExists = useMemo(() => {
    return (
      customVizContext.result.resultType === 'ok' &&
      !!customVizContext.result.value &&
      !CopilotOutput.safeParse(customVizContext.result.value).success
    );
  }, [customVizContext.result]);

  const showError = useMemo(() => {
    return (
      status === 'fatal-error' ||
      customVizContext.result.resultType === 'err' ||
      outputParsingErrorExists
    );
  }, [status, customVizContext.result, outputParsingErrorExists]);

  const showLoading = checkLoadingStatus({ status });

  const currentlySelectedSubject = useMemo(() => {
    if (!currentlySelectedSubjectId) {
      return null;
    }
    return (
      subjectsWithSlateStatus.find(
        (subject) => subject.id === currentlySelectedSubjectId,
      ) ?? null
    );
  }, [subjectsWithSlateStatus, currentlySelectedSubjectId]);

  const handleClickOnGraph = useCallback(
    (subjectId: string | null) => {
      setCurrentlySelectedSubjectId(subjectId);
      setShowGraphTooltip(true);
      setNextSubjectIdToScrollTo(subjectId);
    },
    [
      setCurrentlySelectedSubjectId,
      setNextSubjectIdToScrollTo,
      setShowGraphTooltip,
    ],
  );

  const onApplyFilters = useCallback(() => {
    if (!canAnalyze || !currentCustomVizRequestToExecute) {
      return;
    }

    doViz(currentCustomVizRequestToExecute);
  }, [canAnalyze, currentCustomVizRequestToExecute, doViz]);

  const LoadingIndicator = ({
    onUserAnalysisCancel,
  }: {
    onUserAnalysisCancel: (request: Nullable<CustomVizRequest>) => void;
  }) => {
    return (
      <div className="absolute z-30 flex items-center justify-center w-full h-full pb-10 bg-gray-100 pr-7 bg-opacity-80">
        <CustomVizCircularSpinner onUserAnalysisCancel={onUserAnalysisCancel} />
      </div>
    );
  };

  return (
    <div className="flex ">
      <UserAnalysesOverlay onUserAnalysisCancel={onUserAnalysisCancel} />

      <ShareLinkModal
        shareLink={currentShareLink}
        showModal={showShareLinkModal}
        setShowModal={setShowShareLinkModal}
      />
      {showLoading && (
        <LoadingIndicator onUserAnalysisCancel={onUserAnalysisCancel} />
      )}
      <div className="relative flex flex-col">
        <CopilotPoolView
          allApplicants={subjectsWithSlateStatus}
          dataPoints={slateCopilotDataPoints}
          showTooltip={showGraphTooltip}
          setShowTooltip={setShowGraphTooltip}
          onAddToSlate={addToSlate}
          onRemoveFromSlate={removeFromSlate}
          selectedSubject={currentlySelectedSubject}
          handleClickOnGraph={handleClickOnGraph}
          departments={departments}
          roles={applicableRoles}
          locations={locations}
          selectedDepartments={selectedDepartments}
          selectedRoles={selectedRoles}
          selectedLocations={selectedLocations}
          setSelectedDepartments={setSelectedDepartments}
          setSelectedRoles={setSelectedRoles}
          setSelectedLocations={setSelectedLocations}
          selectedStartDate={selectedStartDate}
          selectedEndDate={selectedEndDate}
          setSelectedStartDate={setSelectedStartDate}
          setSelectedEndDate={setSelectedEndDate}
          canApplyFilters={canAnalyze}
          onApplyFilters={onApplyFilters}
          showError={showError}
          focusArea={focusArea}
          customVizStatus={customVizContext.status}
          customVizResultCreatedAt={customVizContext.resultCreatedAt}
        />
      </div>
    </div>
  );
};
